// src/constants/permissions.js
// ===== PERMISSION DEFINITIONS =====
// Auto-generated from service configuration + manual additions

// Core permission structure: RESOURCE:ACTION
export const PERMISSIONS = {
  // ===== AUTHENTICATION PERMISSIONS =====
  AUTH_PROFILE: "auth:profile",

  // ===== USER PERMISSIONS =====
  USER_VIEW: "user:view",
  USER_CREATE: "user:create",
  USER_EDIT: "user:edit",
  USER_DELETE: "user:delete",
  USER_BULK: "user:bulk",
  USER_EXPORT: "user:export",
  USER_IMPORT: "user:import",

  // ===== DASHBOARD PERMISSIONS =====
  DASHBOARD_VIEW: "dashboard:view",
  DASHBOARD_ANALYTICS: "dashboard:analytics",
  DASHBOARD_EXPORT: "dashboard:export",

  // ===== REPORT PERMISSIONS =====
  REPORT_VIEW: "report:view",
  REPORT_CREATE: "report:create",
  REPORT_EDIT: "report:edit",
  REPORT_DELETE: "report:delete",
  REPORT_EXPORT: "report:export",
  REPORT_SCHEDULE: "report:schedule",

  // ===== SETTINGS PERMISSIONS =====
  SETTING_VIEW: "setting:view",
  SETTING_EDIT: "setting:edit",

  // ===== NOTIFICATION PERMISSIONS =====
  NOTIFICATION_VIEW: "notification:view",
  NOTIFICATION_CREATE: "notification:create",
  NOTIFICATION_UPDATE: "notification:update",
  NOTIFICATION_DELETE: "notification:delete",

  // ===== FILE PERMISSIONS =====
  FILE_VIEW: "file:view",
  FILE_UPLOAD: "file:upload",
  FILE_DOWNLOAD: "file:download",
  FILE_DELETE: "file:delete",

  // ===== SYSTEM PERMISSIONS =====
  SYSTEM_MONITOR: "system:monitor",
  SYSTEM_ADMIN: "system:admin",
  SYSTEM_CONFIG: "system:config",
  SYSTEM_LOGS: "system:logs",

  // ===== ADVANCED PERMISSIONS =====
  AUDIT_VIEW: "audit:view",
  AUDIT_EXPORT: "audit:export",

  ANALYTICS_VIEW: "analytics:view",
  ANALYTICS_EXPORT: "analytics:export",

  API_ACCESS: "api:access",
  API_ADMIN: "api:admin",

  // ===== ROLE MANAGEMENT PERMISSIONS =====
  ROLE_VIEW: "role:view",
  ROLE_CREATE: "role:create",
  ROLE_EDIT: "role:edit",
  ROLE_DELETE: "role:delete",
  ROLE_ASSIGN: "role:assign",
};

// ===== PERMISSION GROUPS =====
// Organize permissions into logical groups for easier management
export const PERMISSION_GROUPS = {
  USER_MANAGEMENT: {
    name: "User Management",
    description: "Permissions for managing users and their data",
    permissions: [
      PERMISSIONS.USER_VIEW,
      PERMISSIONS.USER_CREATE,
      PERMISSIONS.USER_EDIT,
      PERMISSIONS.USER_DELETE,
      PERMISSIONS.USER_BULK,
      PERMISSIONS.USER_EXPORT,
      PERMISSIONS.USER_IMPORT,
    ],
  },

  DASHBOARD_ACCESS: {
    name: "Dashboard Access",
    description: "Access to dashboard and analytics features",
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.DASHBOARD_ANALYTICS,
      PERMISSIONS.DASHBOARD_EXPORT,
    ],
  },

  REPORTING: {
    name: "Reporting",
    description: "Generate and manage reports",
    permissions: [
      PERMISSIONS.REPORT_VIEW,
      PERMISSIONS.REPORT_CREATE,
      PERMISSIONS.REPORT_EDIT,
      PERMISSIONS.REPORT_DELETE,
      PERMISSIONS.REPORT_EXPORT,
      PERMISSIONS.REPORT_SCHEDULE,
    ],
  },

  SYSTEM_ADMINISTRATION: {
    name: "System Administration",
    description: "System-level administration permissions",
    permissions: [
      PERMISSIONS.SYSTEM_MONITOR,
      PERMISSIONS.SYSTEM_ADMIN,
      PERMISSIONS.SYSTEM_CONFIG,
      PERMISSIONS.SYSTEM_LOGS,
      PERMISSIONS.SETTING_VIEW,
      PERMISSIONS.SETTING_EDIT,
    ],
  },

  FILE_MANAGEMENT: {
    name: "File Management",
    description: "Upload, download, and manage files",
    permissions: [
      PERMISSIONS.FILE_VIEW,
      PERMISSIONS.FILE_UPLOAD,
      PERMISSIONS.FILE_DOWNLOAD,
      PERMISSIONS.FILE_DELETE,
    ],
  },

  NOTIFICATIONS: {
    name: "Notifications",
    description: "Manage notifications and alerts",
    permissions: [
      PERMISSIONS.NOTIFICATION_VIEW,
      PERMISSIONS.NOTIFICATION_CREATE,
      PERMISSIONS.NOTIFICATION_UPDATE,
      PERMISSIONS.NOTIFICATION_DELETE,
    ],
  },

  ROLE_MANAGEMENT: {
    name: "Role Management",
    description: "Manage roles and permission assignments",
    permissions: [
      PERMISSIONS.ROLE_VIEW,
      PERMISSIONS.ROLE_CREATE,
      PERMISSIONS.ROLE_EDIT,
      PERMISSIONS.ROLE_DELETE,
      PERMISSIONS.ROLE_ASSIGN,
    ],
  },

  AUDIT_AND_ANALYTICS: {
    name: "Audit & Analytics",
    description: "Access to audit logs and advanced analytics",
    permissions: [
      PERMISSIONS.AUDIT_VIEW,
      PERMISSIONS.AUDIT_EXPORT,
      PERMISSIONS.ANALYTICS_VIEW,
      PERMISSIONS.ANALYTICS_EXPORT,
    ],
  },
};

// ===== PERMISSION HIERARCHIES =====
// Define permission inheritance and dependencies
export const PERMISSION_HIERARCHIES = {
  // If user has the key permission, they automatically get the value permissions
  [PERMISSIONS.USER_DELETE]: [PERMISSIONS.USER_VIEW], // Can't delete without viewing
  [PERMISSIONS.USER_EDIT]: [PERMISSIONS.USER_VIEW], // Can't edit without viewing
  [PERMISSIONS.USER_EXPORT]: [PERMISSIONS.USER_VIEW], // Can't export without viewing

  [PERMISSIONS.REPORT_DELETE]: [PERMISSIONS.REPORT_VIEW],
  [PERMISSIONS.REPORT_EDIT]: [PERMISSIONS.REPORT_VIEW],
  [PERMISSIONS.REPORT_EXPORT]: [PERMISSIONS.REPORT_VIEW],
  [PERMISSIONS.REPORT_SCHEDULE]: [
    PERMISSIONS.REPORT_CREATE,
    PERMISSIONS.REPORT_VIEW,
  ],

  [PERMISSIONS.SYSTEM_ADMIN]: [
    PERMISSIONS.SYSTEM_MONITOR,
    PERMISSIONS.SETTING_VIEW,
    PERMISSIONS.SETTING_EDIT,
  ],

  [PERMISSIONS.DASHBOARD_ANALYTICS]: [PERMISSIONS.DASHBOARD_VIEW],
  [PERMISSIONS.DASHBOARD_EXPORT]: [PERMISSIONS.DASHBOARD_VIEW],

  [PERMISSIONS.ROLE_ASSIGN]: [PERMISSIONS.ROLE_VIEW],
  [PERMISSIONS.ROLE_EDIT]: [PERMISSIONS.ROLE_VIEW],
  [PERMISSIONS.ROLE_DELETE]: [PERMISSIONS.ROLE_VIEW],
};

// ===== PERMISSION METADATA =====
// Additional information about permissions
export const PERMISSION_METADATA = {
  [PERMISSIONS.USER_VIEW]: {
    name: "View Users",
    description: "Can view user profiles and basic information",
    category: "Users",
    risk: "low",
    commonlyUsed: true,
  },

  [PERMISSIONS.USER_CREATE]: {
    name: "Create Users",
    description: "Can create new user accounts",
    category: "Users",
    risk: "medium",
    commonlyUsed: true,
  },

  [PERMISSIONS.USER_EDIT]: {
    name: "Edit Users",
    description: "Can modify existing user information",
    category: "Users",
    risk: "medium",
    commonlyUsed: true,
  },

  [PERMISSIONS.USER_DELETE]: {
    name: "Delete Users",
    description: "Can permanently remove user accounts",
    category: "Users",
    risk: "high",
    commonlyUsed: false,
  },

  [PERMISSIONS.DASHBOARD_VIEW]: {
    name: "View Dashboard",
    description: "Access to main dashboard interface",
    category: "Dashboard",
    risk: "low",
    commonlyUsed: true,
  },

  [PERMISSIONS.DASHBOARD_ANALYTICS]: {
    name: "Advanced Analytics",
    description: "Access to detailed analytics and reports",
    category: "Dashboard",
    risk: "low",
    commonlyUsed: false,
  },

  [PERMISSIONS.SYSTEM_ADMIN]: {
    name: "System Administrator",
    description: "Full system administration privileges",
    category: "System",
    risk: "critical",
    commonlyUsed: false,
  },

  [PERMISSIONS.REPORT_CREATE]: {
    name: "Create Reports",
    description: "Can generate new reports",
    category: "Reports",
    risk: "low",
    commonlyUsed: true,
  },

  [PERMISSIONS.FILE_UPLOAD]: {
    name: "Upload Files",
    description: "Can upload files to the system",
    category: "Files",
    risk: "medium",
    commonlyUsed: true,
  },
};

// ===== UTILITY FUNCTIONS =====

/**
 * Get all permissions for a specific category/resource
 * @param {string} resource - The resource name (e.g., 'user', 'dashboard')
 * @returns {string[]} Array of permissions
 */
export const getPermissionsByResource = (resource) => {
  return Object.values(PERMISSIONS).filter((permission) =>
    permission.startsWith(`${resource}:`)
  );
};

/**
 * Get permission metadata
 * @param {string} permission - The permission string
 * @returns {object|null} Permission metadata or null if not found
 */
export const getPermissionMetadata = (permission) => {
  return PERMISSION_METADATA[permission] || null;
};

/**
 * Get all permissions that depend on a given permission
 * @param {string} permission - The base permission
 * @returns {string[]} Array of dependent permissions
 */
export const getDependentPermissions = (permission) => {
  return PERMISSION_HIERARCHIES[permission] || [];
};

/**
 * Expand permissions to include all dependent permissions
 * @param {string[]} permissions - Array of permissions
 * @returns {string[]} Expanded array with dependencies
 */
export const expandPermissions = (permissions) => {
  const expanded = new Set(permissions);

  permissions.forEach((permission) => {
    const dependencies = getDependentPermissions(permission);
    dependencies.forEach((dep) => expanded.add(dep));
  });

  return Array.from(expanded);
};

/**
 * Check if a permission is high risk
 * @param {string} permission - The permission to check
 * @returns {boolean} True if high risk
 */
export const isHighRiskPermission = (permission) => {
  const metadata = getPermissionMetadata(permission);
  return metadata?.risk === "high" || metadata?.risk === "critical";
};

/**
 * Get commonly used permissions
 * @returns {string[]} Array of commonly used permissions
 */
export const getCommonlyUsedPermissions = () => {
  return Object.entries(PERMISSION_METADATA)
    .filter(([, metadata]) => metadata.commonlyUsed)
    .map(([permission]) => permission);
};

/**
 * Group permissions by category
 * @param {string[]} permissions - Array of permissions to group
 * @returns {object} Grouped permissions by category
 */
export const groupPermissionsByCategory = (permissions) => {
  const grouped = {};

  permissions.forEach((permission) => {
    const metadata = getPermissionMetadata(permission);
    const category = metadata?.category || "Other";

    if (!grouped[category]) {
      grouped[category] = [];
    }

    grouped[category].push(permission);
  });

  return grouped;
};

/**
 * Get permission display name
 * @param {string} permission - The permission string
 * @returns {string} Human-readable permission name
 */
export const getPermissionDisplayName = (permission) => {
  const metadata = getPermissionMetadata(permission);
  if (metadata?.name) {
    return metadata.name;
  }

  // Fallback: convert permission string to readable format
  return permission
    .split(":")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

/**
 * Validate permission string format
 * @param {string} permission - The permission to validate
 * @returns {boolean} True if valid format
 */
export const isValidPermission = (permission) => {
  return /^[a-z]+:[a-z]+$/.test(permission);
};

/**
 * Get all unique resources from permissions
 * @returns {string[]} Array of resource names
 */
export const getAllResources = () => {
  const resources = new Set();

  Object.values(PERMISSIONS).forEach((permission) => {
    const [resource] = permission.split(":");
    resources.add(resource);
  });

  return Array.from(resources).sort();
};

/**
 * Get all unique actions from permissions
 * @returns {string[]} Array of action names
 */
export const getAllActions = () => {
  const actions = new Set();

  Object.values(PERMISSIONS).forEach((permission) => {
    const [, action] = permission.split(":");
    actions.add(action);
  });

  return Array.from(actions).sort();
};

// ===== PERMISSION CONSTANTS FOR EASY IMPORT =====
// These can be used directly in components without importing the entire PERMISSIONS object

// User permissions
export const USER_PERMISSIONS = {
  VIEW: PERMISSIONS.USER_VIEW,
  CREATE: PERMISSIONS.USER_CREATE,
  EDIT: PERMISSIONS.USER_EDIT,
  DELETE: PERMISSIONS.USER_DELETE,
  BULK: PERMISSIONS.USER_BULK,
  EXPORT: PERMISSIONS.USER_EXPORT,
  IMPORT: PERMISSIONS.USER_IMPORT,
};

// Dashboard permissions
export const DASHBOARD_PERMISSIONS = {
  VIEW: PERMISSIONS.DASHBOARD_VIEW,
  ANALYTICS: PERMISSIONS.DASHBOARD_ANALYTICS,
  EXPORT: PERMISSIONS.DASHBOARD_EXPORT,
};

// Report permissions
export const REPORT_PERMISSIONS = {
  VIEW: PERMISSIONS.REPORT_VIEW,
  CREATE: PERMISSIONS.REPORT_CREATE,
  EDIT: PERMISSIONS.REPORT_EDIT,
  DELETE: PERMISSIONS.REPORT_DELETE,
  EXPORT: PERMISSIONS.REPORT_EXPORT,
  SCHEDULE: PERMISSIONS.REPORT_SCHEDULE,
};

// System permissions
export const SYSTEM_PERMISSIONS = {
  MONITOR: PERMISSIONS.SYSTEM_MONITOR,
  ADMIN: PERMISSIONS.SYSTEM_ADMIN,
  CONFIG: PERMISSIONS.SYSTEM_CONFIG,
  LOGS: PERMISSIONS.SYSTEM_LOGS,
};

// Export all permissions as default
export default PERMISSIONS;
